import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
  when,
} from 'mobx'
import { Cubit } from './core'
import type { FirebaseRepository } from '../models/FirebaseRepository'
import type { StaticModelCollection } from '../firestore-mobx/model'
import { Section } from '../models/Section'
import {
  getSection,
  getSections,
  getSectionsStreamForInstructors,
} from '../firestore/Section'
import { RoomState, RoomStateStatus } from '../models/RoomState'
import {
  getRoomStatesForStudent,
  getRoomWasGraded,
} from '../firestore/RoomState'
import { getSlideDeck } from '../firestore/SlideDeck'
import { SlideDeck } from '../models/SlideDeck'
import { SectionAssignment } from '../models/SectionAssignment'
import { AssignmentType } from '../models/SectionAssignment'
import { getSectionAssignments } from '../firestore/SectionAssignment'
import {
  ValidLibraryObject,
  ValidLibraryObjectActionState,
} from '../stores/ValidLibraryObject'
import { LibraryObjectState } from '../types'
import { RoomStateAnswer } from '../models/RoomStateAnswer'
import { SlideQuestion } from '../models/SlideQuestion'
import { fetchRoomStateAnswers } from '../firestore/RoomStateAnswer'
import {
  SLIDE_DECK_SUMMARIES_MAX_IDS,
  type SlideDeckInclude,
} from '../types/slideDeckAccess'
import { SlideModel } from '../models/SlideModel'
import { convertApiDocsToModels } from '../firestore-mobx/apiModelHelper'
import { getSettingsSectionPassPricing } from '../firestore/SettingsSectionPassPricing'
import { SettingsSectionPassPricing } from '../models/SettingsSectionPassPricing'
import { CollectionSnapshotStreamCollector } from '../firestore-mobx/stream'
import type { BreakoutUser } from '../models/BreakoutUser'
import { checkUserYetToCompletePeerReview } from '../firestore/RoomStateUPR'
import { guardOn, type LoadingState } from '../util/guardOn'
import { captureException } from '@sentry/core'

export class StudentLibraryCubit extends Cubit {
  repository: FirebaseRepository
  impersonatedBreakoutUser: BreakoutUser

  sections: StaticModelCollection<Section>
  roomStates: StaticModelCollection<RoomState>
  slideDecksById = observable.map<string, SlideDeck>()
  assignmentsBySectionId = observable.map<
    string,
    StaticModelCollection<SectionAssignment>
  >()

  userAnswersByRoomId = observable.map<
    string,
    StaticModelCollection<RoomStateAnswer>
  >()

  slideQuestionsBySlideDeckId = observable.map<
    string,
    StaticModelCollection<SlideQuestion>
  >()

  // Fetched alongside the questions (same call) so the End-of-Session slide can
  // be identified — a quiz question only records its slideId, and quiz-type
  // questions also live on solo/interactive slides.
  slidesBySlideDeckId = observable.map<
    string,
    StaticModelCollection<SlideModel>
  >()

  roomHasGradingResults = observable.map<string, boolean>()

  sectionPassPricing: SettingsSectionPassPricing

  // we keep track of what we've fetched so we don't fetch it again
  // we write here when we start the request so we don't start
  // it if its already in progress but not resolved
  private _fetchedAnswersForRoomId = new Set<string>()
  private _fetchedQuestionsForSlideDeckId = new Set<string>()
  // Slides are fetched separately from questions: they are only needed for
  // the End-of-Session window, and a deck's questions are often already
  // cached from the pre-work path by the time that window opens.
  private _fetchedSlidesForSlideDeckId = new Set<string>()
  // v2 only: decks whose summary read has started (v1 dedupes by stream name).
  private _fetchedSlideDeckIds = new Set<string>()

  @observable assignmentFilterSearchTerm: string = ''
  @observable assignmentFilter: StudentLibraryAssignmentFilter = 'current'
  @observable sortingStrategy: StudentLibraryAssignmentSortingStrategy =
    'dueDate'

  @observable sortingOrder: StudentLibraryAssignmentSortingOrder = 'asc'

  // on first load if we want to wait for all userAnswers/slideQuestions to load to avoid flashing various states as they come in
  // afterwards if don't have the data yet we display the next eligible state
  libraryObjectForHeaderDidResolve = false

  // Backwards compatibility for old version of student library
  @observable showCompleted = false

  @observable private _didLoadingComplete = false

  @observable private _roomsWithOutstandingPeerReview: string[] | 'pending' =
    'pending'

  @computed
  get roomsWithOutstandingPeerReview(): LoadingState<string[]> {
    const userId = this.impersonatedBreakoutUser.uid
    const cutoffDate = this.earliestNonExpiredRoomDate
    const needsFetch = !!userId && !!cutoffDate
    const fetchComplete = this._roomsWithOutstandingPeerReview !== 'pending'

    return guardOn(this.isLoaded && (!needsFetch || fetchComplete), () => {
      if (needsFetch) {
        return this._roomsWithOutstandingPeerReview as string[] // Type assertion as guardOn isn't smart enough
      }
      return []
    })
  }

  sectionId?: string

  constructor(
    repository: FirebaseRepository,
    impersonatedBreakoutUser: BreakoutUser,
    {
      sectionId,
    }: {
      sectionId?: string
    } = {}
  ) {
    super()
    makeObservable(this)

    this.repository = repository
    this.impersonatedBreakoutUser = impersonatedBreakoutUser

    this.sectionId = sectionId

    this.sections = Section.emptyCollection(repository)
    this.roomStates = RoomState.emptyCollection(repository)
    this.sectionPassPricing = SettingsSectionPassPricing.empty(repository)
  }

  initialized = false
  async initialize(): Promise<void> {
    if (this.initialized) return
    this.initialized = true
    const user = this.impersonatedBreakoutUser
    user.loadUserProfileTokenData()

    // v2: once every section's assignments have loaded, read all their decks
    // in one batched request instead of one live Firestore stream per deck.
    // Later assignment changes only request decks not requested yet.
    if (this.repository.readsSlideDeckFromV2) {
      this.addReaction({
        whenThisChanges: () =>
          this.allSectionAssignmentsLoaded
            ? this.allSectionAssignments.map(
                (assignment) => assignment.data.slideDeckId
              )
            : [],
        thenRunThisCode: (slideDeckIds) =>
          this.fetchSlideDecksViaApi(slideDeckIds),
      })
    }
    this.addStream(
      getSettingsSectionPassPricing(this.repository),
      (pricing) =>
        this.sectionPassPricing.replaceModel(
          pricing.hasData
            ? pricing
            : SettingsSectionPassPricing.empty(this.repository)
        ),
      {
        name: 'section-pass-pricing',
      }
    )

    // LTI specific - show assignments only for specified section
    if (this.sectionId) {
      this.addStream(
        getSection(this.repository, { sectionId: this.sectionId }),
        (section) => {
          this.sections.replaceModels([section])
          this.addSectionAssignmentStream(section.id)
        },
        {
          name: 'sections',
          onError: (error) => {
            console.error('Error getting sections', error)
            this.sections.replaceModels([])
          },
        }
      )
    } else {
      // Load sections for assisted instructors (if any), combined with the user's own sections
      if (user) {
        await when(() => user.usersIAssist.isLoaded)
      }
      const assistedInstructorIds =
        user.usersIAssist.value?.map((i) => i.id) ?? []

      if (assistedInstructorIds.length > 0) {
        const assistedSectionsStream = getSectionsStreamForInstructors(
          this.repository,
          assistedInstructorIds
        )
        const studentOrInstructorSectionsStream = getSections(this.repository, {
          userId: user.uid,
        })
        const streamCollector = new CollectionSnapshotStreamCollector<Section>({
          resolveAfterAllLoaded: true,
        })
        streamCollector.attachStream(assistedSectionsStream)
        streamCollector.attachStream(studentOrInstructorSectionsStream)

        this.addStream(
          streamCollector.stream,
          (_sections) => {
            // Dedupe since we're combining streams:
            const sections = Array.from(
              new Map(_sections.map((s) => [s.id, s])).values()
            )
            this.sections.replaceModels(sections)
            for (const section of sections) {
              this.addSectionAssignmentStream(section.id)
            }
          },
          {
            name: 'sections',
            onError: (error) => {
              console.error('Error getting sections', error)
              this.sections.replaceModels([])
            },
          }
        )
      } else {
        this.addStream(
          getSections(this.repository, {
            userId: user.uid,
          }),
          (sections) => {
            this.sections.replaceModels(sections)

            for (const section of sections) {
              this.addSectionAssignmentStream(section.id)
            }
          },
          {
            name: 'sections',
            onError: (error) => {
              console.error('Error getting sections', error)
              this.sections.replaceModels([])
            },
          }
        )
      }
    }

    this.addStream(
      getRoomStatesForStudent(this.repository, {
        sectionId: this.sectionId,
      }),
      (roomStates) => {
        this.roomStates.replaceModels(roomStates)
        this.startCheckRoomGradedStreamsForRooms(roomStates)
      },
      {
        name: 'room-states',
        onError: (error) => {
          console.error('Error getting room states', error)
          this.roomStates.replaceModels([])
        },
      }
    )

    this.addReaction({
      whenThisChanges: () => ({
        loaded: this.isLoaded,
        libraryObjects: this.libraryObjectsWhereQuizDataIsRequired,
      }),
      thenRunThisCode: ({ libraryObjects, loaded }) => {
        if (!loaded) return
        const roomIds = new Set<string>()
        const slideDeckContexts = new Map<
          string,
          {
            sectionId: string
            assignmentId: string
            slideDeckId: string
            needsSlides: boolean
          }
        >()
        libraryObjects.forEach((libObj) => {
          if (!libObj.quizDataRequiredForActionState) return
          // room-less solo objects need questions (keyed by the assignment's
          // deck) but have no room to fetch answers for
          if (libObj.roomState.isNotEmpty) roomIds.add(libObj.roomState.id)
          const slideDeckId = libObj.roomState.isNotEmpty
            ? libObj.roomState.slideDeckId
            : libObj.assignment.data.slideDeckId
          const existing = slideDeckContexts.get(slideDeckId)
          if (existing) {
            // another room may share this deck and need slides even if the
            // first one we saw did not
            existing.needsSlides ||= libObj.endOfSessionWindowIsOpen
            return
          }
          slideDeckContexts.set(slideDeckId, {
            sectionId: libObj.section.id,
            assignmentId: libObj.assignment.id,
            slideDeckId,
            needsSlides: libObj.endOfSessionWindowIsOpen,
          })
        })
        this.fetchQuizDataForRoomStatesAndSlideDecks(
          Array.from(roomIds),
          Array.from(slideDeckContexts.values())
        )
      },
    })

    // Trigger peer review fetch when we have all the required data
    this.disposers.push(
      when(
        () => this.isLoaded && !!user.uid && !!this.earliestNonExpiredRoomDate,
        () => {
          checkUserYetToCompletePeerReview(this.repository, {
            userId: user.uid,
            cutoffDate: this.earliestNonExpiredRoomDate!,
          })
            .then((roomIds) => {
              runInAction(() => {
                this._roomsWithOutstandingPeerReview = roomIds
              })
            })
            .catch((error) => {
              console.error('Error checking peer review status', error)
              runInAction(() => {
                this._roomsWithOutstandingPeerReview = []
              })
            })
        }
      )
    )
  }

  addSectionAssignmentStream(sectionId: string): void {
    const key = `section-assignments-${sectionId}`
    this.addStream(
      getSectionAssignments(this.repository, {
        sectionId,
        onlyPublished: true,
      }),
      (assignments) => {
        const existing = this.assignmentsBySectionId.get(sectionId)
        if (existing) {
          existing.replaceModels(assignments)
        } else {
          const initial = SectionAssignment.emptyCollection(this.repository)
          initial.replaceModels(assignments)
          runInAction(() => {
            this.assignmentsBySectionId.set(sectionId, initial)
          })
        }

        // v2 reads the decks in batches; see initialize.
        if (!this.repository.readsSlideDeckFromV2) {
          for (const assignment of assignments) {
            this.addSlideDeckStream(assignment.data.slideDeckId)
          }
        }
      },
      {
        name: key,
        onError: (error) => {
          console.error('Error getting assignments', error)
          this.roomStates.replaceModels([])
          // if it's an error, we want to set it to empty so it still counts as fetched
          const empty = SectionAssignment.emptyCollection(this.repository)
          runInAction(() => {
            this.assignmentsBySectionId.set(sectionId, empty)
          })
        },
      }
    )
  }

  addSlideDeckStream(slideDeckId: string): void {
    const slideDeckKey = `slide-deck-${slideDeckId}`
    // only add slide deck it once
    if (!this.hasStream(slideDeckKey)) {
      this.addStream(
        getSlideDeck(this.repository, { slideDeckId: slideDeckId }),
        (slideDeck) => {
          runInAction(() => {
            this.slideDecksById.set(slideDeck.id, slideDeck)
          })
        },
        {
          name: slideDeckKey,
          onError: (error) => {
            console.error(
              'Error getting slide deck for room state',
              slideDeckId,
              error
            )
            // if it's an error, we want to set it to empty so it still counts
            // and the page can render, even though this slide deck will be missing
            const empty = SlideDeck.empty(this.repository)
            runInAction(() => {
              this.slideDecksById.set(slideDeckId, empty)
            })
          },
        }
      )
    }
  }

  private fetchSlideDecksViaApi(slideDeckIds: string[]) {
    const newSlideDeckIds = Array.from(new Set(slideDeckIds)).filter(
      (slideDeckId) => !this._fetchedSlideDeckIds.has(slideDeckId)
    )
    for (const slideDeckId of newSlideDeckIds) {
      this._fetchedSlideDeckIds.add(slideDeckId)
    }

    for (
      let start = 0;
      start < newSlideDeckIds.length;
      start += SLIDE_DECK_SUMMARIES_MAX_IDS
    ) {
      void this.fetchSlideDeckSummariesWithRetry(
        newSlideDeckIds.slice(start, start + SLIDE_DECK_SUMMARIES_MAX_IDS)
      )
    }
  }

  // Every requested id ends up in slideDecksById, because the list waits for
  // all of them.
  private async fetchSlideDeckSummariesWithRetry(
    slideDeckIds: string[],
    retryCount: number = 0
  ): Promise<void> {
    if (this.isDisposed) return

    try {
      const { slideDecks } =
        await this.repository.fetchSlideDeckSummaries(slideDeckIds)
      if (this.isDisposed) return

      const models = slideDecks.map((doc) =>
        SlideDeck.buildModelFromApiResponse(this.repository, doc)
      )
      runInAction(() => {
        for (const slideDeck of models) {
          this.slideDecksById.set(slideDeck.id, slideDeck)
        }
        // The server leaves out decks that don't exist or that the user can't
        // read (drafts). The v1 Firestore stream errors on both, and its error
        // path does the same: an empty deck counts as loaded, and the list
        // leaves that assignment out.
        for (const slideDeckId of slideDeckIds) {
          if (this.slideDecksById.has(slideDeckId)) continue
          this.slideDecksById.set(slideDeckId, SlideDeck.empty(this.repository))
        }
      })
    } catch (err) {
      if (this.isDisposed) return

      if (retryCount < 3) {
        const delay = 400 * retryCount
        await new Promise((resolve) => setTimeout(resolve, delay))
        return this.fetchSlideDeckSummariesWithRetry(
          slideDeckIds,
          retryCount + 1
        )
      }

      console.error('Error fetching slide deck summaries', err)
      captureException(err, {
        tags: { feature: 'slide-deck-api' },
        extra: { slideDeckIds },
      })
      runInAction(() => {
        for (const slideDeckId of slideDeckIds) {
          this.slideDecksById.set(slideDeckId, SlideDeck.empty(this.repository))
        }
      })
    }
  }

  // Backwards compatibility for old version of student library
  @action
  toggleShowCompleted() {
    this.showCompleted = !this.showCompleted
  }

  @action quizDataForRoomState(roomState: RoomState): {
    slideQuestions: StaticModelCollection<SlideQuestion>
    userAnswers: StaticModelCollection<RoomStateAnswer>
  } {
    return {
      slideQuestions:
        this.slideQuestionsBySlideDeckId.get(roomState.slideDeckId) ||
        SlideQuestion.emptyCollection(this.repository),
      userAnswers:
        this.userAnswersByRoomId.get(roomState.id) ||
        RoomStateAnswer.emptyCollection(this.repository),
    }
  }

  @action
  /**
   * This function is called when the room states are loaded
   * only start streams for the rooms in which we need to know if they are graded
   */
  private startCheckRoomGradedStreamsForRooms(roomStates: RoomState[]) {
    roomStates.forEach(
      async ({
        id,
        data: { sectionId, activeSlide, assignmentId, slideDeckId },
      }) => {
        const roomGradingStreamKey = `room-grading-${id}`

        // if already streaming or
        if (this.hasStream(roomGradingStreamKey)) return

        // it not active slide or assignment, we don't need to check grading
        if (!sectionId || !activeSlide || !assignmentId || !slideDeckId) return //return this.roomHasGradingResults.set(id, false)

        // we need the slide deck to determine if we need last slide
        await when(() => this.slideDecksById.has(slideDeckId))

        const slideCount =
          this.slideDecksById.get(slideDeckId)?.data.slideDeckSlideCount

        const onLastSlide = slideCount && activeSlide >= slideCount - 1

        // if assignment is not expired, or on last slide, we don't need to check grading
        // as we know the room is complete
        if (onLastSlide) return

        // start streaming the grading results for this room
        this.addStream(
          getRoomWasGraded(this.repository, { roomStateId: id }),
          (graded) => {
            this.roomHasGradingResults.set(id, graded)
          },
          {
            name: roomGradingStreamKey,
            // Students may lose access to a room's engagement subcollection
            // (e.g. removed from the room). Silence the resulting permission
            // errors instead of reporting them to Sentry.
            disableCaptureException: true,
          }
        )
      }
    )
  }

  @action
  private fetchQuizDataForRoomStatesAndSlideDecks(
    roomIds: string[],
    slideDeckContexts: {
      sectionId: string
      assignmentId: string
      slideDeckId: string
      needsSlides: boolean
    }[]
  ) {
    const userId = this.repository.currentUser?.uid
    // i don't think we can reach this error, mostly here for type safety
    if (!userId) throw new Error('user must be logged in')

    slideDeckContexts.forEach((ctx) => {
      // Tracked independently so a deck whose questions were already fetched
      // for the pre-work path can still pick up slides once one of its rooms
      // reaches the End-of-Session window.
      const wantQuestions = !this._fetchedQuestionsForSlideDeckId.has(
        ctx.slideDeckId
      )
      const wantSlides =
        ctx.needsSlides &&
        !this._fetchedSlidesForSlideDeckId.has(ctx.slideDeckId)
      if (!wantQuestions && !wantSlides) return
      if (wantQuestions)
        this._fetchedQuestionsForSlideDeckId.add(ctx.slideDeckId)
      if (wantSlides) this._fetchedSlidesForSlideDeckId.add(ctx.slideDeckId)
      this.fetchSlideDeckDataWithRetry(ctx, {
        questions: wantQuestions,
        slides: wantSlides,
      })
    })

    roomIds.forEach((roomId) => {
      if (this._fetchedAnswersForRoomId.has(roomId)) return
      this._fetchedAnswersForRoomId.add(roomId)
      this.fetchRoomStateAnswersWithRetry(roomId, userId)
    })
  }

  private async fetchSlideDeckDataWithRetry(
    ctx: { sectionId: string; assignmentId: string; slideDeckId: string },
    want: { questions: boolean; slides: boolean },
    retryCount: number = 0
  ): Promise<void> {
    if (this.isDisposed) return

    try {
      const include: SlideDeckInclude[] = []
      if (want.questions) include.push('questions')
      if (want.slides) include.push('slides')

      const result = await this.repository.fetchSlideDeckForAssignment({
        sectionId: ctx.sectionId,
        assignmentId: ctx.assignmentId,
        slideDeckId: ctx.slideDeckId,
        include,
        callsite: 'StudentLibraryCubit.fetchSlideDeckDataWithRetry',
        reason: 'end-of-session-quiz-data',
      })

      if (this.isDisposed) return

      if (!result.success) {
        throw new Error(`getSlideDeckForAssignment failed: ${result.error}`)
      }

      runInAction(() => {
        // the API returns loosely-typed ApiDoc[], but convertApiDocsToModels
        // wants the model's concrete data type — cast through `never` to bridge the gap
        if (want.questions) {
          const questions = result.questions
            ? convertApiDocsToModels(
                this.repository,
                result.questions as { id: string; data: never }[],
                SlideQuestion
              )
            : []

          const collection = SlideQuestion.emptyCollection(this.repository)
          collection.replaceModels(questions)
          this.slideQuestionsBySlideDeckId.set(ctx.slideDeckId, collection)
        }

        if (want.slides) {
          const slides = result.slides
            ? convertApiDocsToModels(
                this.repository,
                result.slides as { id: string; data: never }[],
                SlideModel
              )
            : []
          const slideCollection = SlideModel.emptyCollection(this.repository)
          slideCollection.replaceModels(slides)
          this.slidesBySlideDeckId.set(ctx.slideDeckId, slideCollection)
        }
      })
    } catch (err) {
      if (this.isDisposed) return

      if (retryCount < 3) {
        const delay = 400 * retryCount
        await new Promise((resolve) => setTimeout(resolve, delay))
        return this.fetchSlideDeckDataWithRetry(ctx, want, retryCount + 1)
      } else {
        // Name what was actually requested — this wrapper fetches questions,
        // slides, or both, so a fixed message misleads when only one failed.
        const requested = [
          ...(want.questions ? ['questions'] : []),
          ...(want.slides ? ['slides'] : []),
        ].join(' and ')
        console.error(`Error fetching slide deck ${requested}`, err)
        captureException(err, {
          tags: { feature: 'student-library-quiz-data' },
          extra: { slideDeckId: ctx.slideDeckId, requested },
        })
        // Set empty collections so UI doesn't hang waiting. replaceModels is
        // what clears the loading flag — a bare emptyCollection() still reports
        // isLoaded === false, which would leave quizData undefined forever.
        runInAction(() => {
          if (want.questions) {
            const collection = SlideQuestion.emptyCollection(this.repository)
            collection.replaceModels([])
            this.slideQuestionsBySlideDeckId.set(ctx.slideDeckId, collection)
          }
          if (want.slides) {
            const slideCollection = SlideModel.emptyCollection(this.repository)
            slideCollection.replaceModels([])
            this.slidesBySlideDeckId.set(ctx.slideDeckId, slideCollection)
            // Release the guard so a later pass can try again. Without this a
            // transient blip would hide the Finish Quiz call-to-action for the
            // rest of the cubit's life, since the deck would stay marked as
            // fetched with an empty slide list. The driving reaction re-runs as
            // the clock ticks, so this retries about once a minute for as long
            // as the End-of-Session window stays open, then stops with it.
            this._fetchedSlidesForSlideDeckId.delete(ctx.slideDeckId)
          }
        })
      }
    }
  }

  private async fetchRoomStateAnswersWithRetry(
    roomId: string,
    userId: string,
    retryCount: number = 0
  ): Promise<void> {
    if (this.isDisposed) return

    try {
      const answers = await fetchRoomStateAnswers(this.repository, {
        roomId,
        userId,
      })
      if (this.isDisposed) return

      const collection = RoomStateAnswer.emptyCollection(this.repository)
      collection.replaceModels(answers)
      runInAction(() => {
        this.userAnswersByRoomId.set(roomId, collection)
      })
    } catch (err) {
      if (this.isDisposed) return

      if (retryCount < 3) {
        const delay = 400 * retryCount
        await new Promise((resolve) => setTimeout(resolve, delay))
        return this.fetchRoomStateAnswersWithRetry(
          roomId,
          userId,
          retryCount + 1
        )
      } else {
        console.error('Error fetching room state answers', err)
        captureException(err, {
          tags: { feature: 'student-library-quiz-data' },
        })
        // Set empty collection so UI doesn't hang waiting — replaceModels is
        // what clears the loading flag (see the slide-question fetch above).
        const collection = RoomStateAnswer.emptyCollection(this.repository)
        collection.replaceModels([])
        runInAction(() => {
          this.userAnswersByRoomId.set(roomId, collection)
        })
      }
    }
  }

  @computed
  get isLoading() {
    if (this._didLoadingComplete) return false

    const loading =
      this.sections.isLoading ||
      this.roomStates.isLoading ||
      this.assignmentsBySectionId.size !== this.sections.models.length ||
      this.sectionPassPricing.isLoading ||
      !this.allSlideDecksLoaded

    if (!loading) runInAction(() => (this._didLoadingComplete = true))

    return loading
  }

  @computed
  get isLoaded() {
    return !this.isLoading
  }

  @computed
  get allSectionAssignmentsLoaded() {
    return (
      !this.sections.isLoading &&
      this.assignmentsBySectionId.size === this.sections.models.length
    )
  }

  @computed
  get allSlideDecksLoaded() {
    // all assignments must be loaded to have all slide decks loaded
    if (this.assignmentsBySectionId.size !== this.sections.models.length)
      return false

    const slideDeckIds = Array.from(
      this.assignmentsBySectionId.values()
    ).flatMap((a) => a.models.map((a) => a.data.slideDeckId))

    return slideDeckIds.every((id) => this.slideDecksById.has(id))
  }

  @computed
  get allSectionAssignments() {
    const assignmentsArray = Array.from(this.assignmentsBySectionId.values())
    return assignmentsArray.flatMap((assignments) => assignments.models)
  }

  @computed
  get earliestNonExpiredRoomDate(): Date | undefined {
    if (this.isLoading) return undefined

    const now = new Date()
    let earliestDate: Date | undefined

    for (const roomState of this.roomStates.models) {
      const assignment = this.allSectionAssignments.find(
        (a) => a.id === roomState.data.assignmentId
      )
      if (!assignment) continue

      const section = this.sections.models.find(
        (s) => s.id === assignment.data.sectionId
      )
      if (!section) continue

      const assignmentExpired =
        assignment.data.expiresAt &&
        assignment.data.expiresAt.getTime() < now.getTime()

      if (!assignmentExpired) {
        const roomDate =
          roomState.data.roomStartedAt ||
          roomState.data.activeSlideChangedAt ||
          roomState.data.scheduledAt

        if (roomDate) {
          if (!earliestDate || roomDate.getTime() < earliestDate.getTime()) {
            earliestDate = roomDate
          }
        }
      }
    }

    return earliestDate
  }

  @computed
  get libraryObjects() {
    // do not compute until we have all data
    if (this.isLoading) return []
    const list: ValidLibraryObject[] = []
    const usedRoomIds = new Set<string>()
    for (const assignment of this.allSectionAssignments) {
      const roomStates = this.roomStates.models.filter(
        (roomState) => roomState.data.assignmentId === assignment.id
      )
      if (
        roomStates.length === 0 &&
        assignment.data.assignmentType === AssignmentType.ManyGroups
      ) {
        const libraryObj = new ValidLibraryObject({
          cubit: this,
          assignmentId: assignment.id,
          sectionId: assignment.data.sectionId,
        })
        list.push(libraryObj)
      } else {
        for (const roomState of roomStates) {
          usedRoomIds.add(roomState.id)
          const libraryObj = new ValidLibraryObject({
            cubit: this,
            assignmentId: assignment.id,
            sectionId: assignment.data.sectionId,
            roomId: roomState.id,
          })
          list.push(libraryObj)
        }
      }
    }

    return list
  }

  @computed get correctLibraryObjects() {
    return this.libraryObjects.filter(
      (libraryObject) => libraryObject.slideDeck.isNotEmpty
    )
  }

  @computed
  get libraryObjectForHeader() {
    if (this.isLoading) return null
    if (!this.libraryObjectForHeaderDidResolve) {
      // if fetches are in progress on the initial load we don't want to render a header yet
      const slideQuestionsLoading =
        this._fetchedQuestionsForSlideDeckId.size >
        this.slideQuestionsBySlideDeckId.size
      const userAnswersLoading =
        this._fetchedAnswersForRoomId.size > this.userAnswersByRoomId.size
      if (
        slideQuestionsLoading ||
        userAnswersLoading ||
        this.roomsWithOutstandingPeerReview.isLoading
      )
        return null
    }

    const roomsWithOutstandingPeerReview = this.roomsWithOutstandingPeerReview
    if (roomsWithOutstandingPeerReview.isLoading) return null

    const actionableLibraryObjects = this.correctLibraryObjects.filter(
      (libraryObject) => {
        const hasOutstandingPeerReview =
          roomsWithOutstandingPeerReview.value.includes(
            libraryObject.roomState.id
          )
        const actionState = libraryObject.getActionState({
          hasOutstandingPeerReview,
        })
        const slideDeck = libraryObject.slideDeck
        return (
          slideDeck.isLoaded &&
          actionTypeHasActionComponentToRender(actionState)
        )
      }
    )

    const sorted = actionableLibraryObjects.sort(
      sortingStrategyFromType('default', roomsWithOutstandingPeerReview.value)
    )

    // return the highest priority actionable object, or null if no actionable objects
    const libraryObjectToReturn = sorted.length ? sorted[0] : null
    if (!libraryObjectToReturn) return null
    this.libraryObjectForHeaderDidResolve = true
    const hasOutstandingPeerReview =
      roomsWithOutstandingPeerReview.value.includes(
        libraryObjectToReturn.roomState.id
      )
    return {
      libraryObject: libraryObjectToReturn,
      actionState: libraryObjectToReturn.getActionState({
        hasOutstandingPeerReview,
      }),
    }
  }

  // chat never applies to solo rooms, so the column renders only when the
  // displayed list contains a non-solo assignment
  @computed
  get showChatColumn() {
    return this.filteredSortedLibraryObjects.some(
      (libraryObject) => !libraryObject.assignment.isSolo
    )
  }

  @computed
  get sortedLibraryObjects() {
    const peerReviewState = this.roomsWithOutstandingPeerReview
    const roomsWithOutstandingPeerReview = peerReviewState.isLoading
      ? []
      : peerReviewState.value

    const results = this.correctLibraryObjects
      .slice()
      .sort(
        sortingStrategyFromType(
          this.sortingStrategy,
          roomsWithOutstandingPeerReview
        )
      )

    return this.sortingOrder === 'asc' ? results : results.reverse()
  }

  // Backwards compatibility for old version of student library
  @computed
  get filteredLibraryObjects() {
    if (!this.showCompleted) {
      return this.sortedLibraryObjects.filter((libraryObject) => {
        return (
          libraryObject.libraryObjectState !== LibraryObjectState.completed &&
          libraryObject.libraryObjectState !== LibraryObjectState.expired
        )
      })
    }
    return this.sortedLibraryObjects
  }

  @computed
  get filteredSortedLibraryObjects() {
    const strategyFilter = filterStrategyFromType(this.assignmentFilter)

    const filterWithSearchTerm = (libraryObject: ValidLibraryObject) => {
      if (this.assignmentFilterSearchTerm) {
        const {
          slideDeck: {
            data: { slideDeckName, slideDeckTeaser },
          },
          section: {
            data: { className, sectionName },
            instructor: { fullName: instructorName },
          },
        } = libraryObject

        const stringToSearch =
          `${slideDeckName} ${slideDeckTeaser} ${className} ${sectionName} ${instructorName}`.toLowerCase()

        if (
          !stringToSearch.includes(
            this.assignmentFilterSearchTerm.trim().toLowerCase()
          )
        ) {
          return false
        }
        return true
      }

      return true
    }

    const filterCanceledAssignmentsWithNoCompletedGroups = (
      libraryObject: ValidLibraryObject
    ) => {
      if (libraryObject.libraryObjectState === LibraryObjectState.canceled) {
        const roomWasGraded = this.roomHasGradingResults.get(
          libraryObject.roomState.id
        )

        const completedGroups = this.roomStates.models.filter(
          (r) =>
            r.data.assignmentId === libraryObject.assignment.id &&
            r.getRoomStateStatus(
              libraryObject.assignment.getEffectiveExpiresAtForUsers(r.userIds),
              libraryObject.section.data.sectionState,
              libraryObject.slideDeck.data.slideDeckSlideCount,
              libraryObject.assignment.data.assignmentState,
              roomWasGraded ?? false,
              libraryObject.assignment.hasFutureIndividualDeadlineForAnyUser(
                r.userIds
              )
            ) === RoomStateStatus.completed
        )
        if (completedGroups.length === 0) {
          return false
        }
      }

      return true
    }

    return this.sortedLibraryObjects
      .filter(strategyFilter)
      .filter(filterWithSearchTerm)
      .filter(filterCanceledAssignmentsWithNoCompletedGroups)
  }

  @computed
  get libraryObjectsWhereQuizDataIsRequired() {
    return this.libraryObjects.filter(
      (libraryObject) => libraryObject.quizDataRequiredForActionState
    )
  }

  @action
  changeAssignmentFilter(filter: StudentLibraryAssignmentFilter) {
    this.assignmentFilter = filter
  }

  @action
  changeSortingStrategy = (
    strategy: StudentLibraryAssignmentSortingStrategy,
    order: 'asc' | 'desc'
  ) => {
    this.sortingStrategy = strategy
    this.sortingOrder = order
  }

  @action
  changeSearchTerm(searchTerm: string) {
    this.assignmentFilterSearchTerm = searchTerm
  }
}

function compareDates(a: Date | undefined, b: Date | undefined) {
  if (!a && !b) return 0
  if (!a) return 1
  if (!b) return -1
  return a.getTime() - b.getTime()
}

function filterStrategyFromType(
  type: StudentLibraryAssignmentFilter
): (libraryObject: ValidLibraryObject) => boolean {
  const now = new Date()

  switch (type) {
    case 'all':
      return () => true
    case 'current':
      return (libraryObject) => {
        if (libraryObject.libraryObjectState === LibraryObjectState.expired) {
          return false
        }
        const { expiresAt } = libraryObject.assignment.data
        if (!expiresAt) {
          return true
        }

        const twoWeeksAgo = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000)
        return expiresAt > twoWeeksAgo
      }
  }
}

function sortingStrategyFromType(
  type: StudentLibraryAssignmentSortingStrategy,
  roomsWithOutstandingPeerReview: string[]
): (a: ValidLibraryObject, b: ValidLibraryObject) => number {
  switch (type) {
    case 'default':
      return (a, b) => {
        const aHasOutstandingPeerReview =
          roomsWithOutstandingPeerReview.includes(a.roomState.id)
        const bHasOutstandingPeerReview =
          roomsWithOutstandingPeerReview.includes(b.roomState.id)
        const aActionState = a.getActionState({
          hasOutstandingPeerReview: aHasOutstandingPeerReview,
        })
        const bActionState = b.getActionState({
          hasOutstandingPeerReview: bHasOutstandingPeerReview,
        })
        const aPriority = getSortingPriorityFromActionState(aActionState)
        const bPriority = getSortingPriorityFromActionState(bActionState)

        // if no date provided use maximum possible date
        const aScheduledAt =
          a.roomState.data.scheduledAt || new Date(8640000000000000)
        const bScheduledAt =
          b.roomState.data.scheduledAt || new Date(8640000000000000)

        // if no date provided use max date (should never happen as only draft assignment can have this state)
        const aDueDate = a.assignment.dueDate || new Date(8640000000000000)
        const bDueDate = b.assignment.dueDate || new Date(8640000000000000)

        if (aPriority === bPriority) {
          // we only have priority collisions on on the following states
          // priority === 0 (join/start session)
          // priority === 3 (enroll/scheduleSession)

          // for startSession/joinSession compare on scheduledTime
          if (aPriority === 0) return compareDates(aScheduledAt, bScheduledAt)
          // for enroll/scheduleSession compare on dueDate
          if (aPriority === 3) return compareDates(aDueDate, bDueDate)

          // if we have a priority collision
          // and same dates maintain order
          return 0
        }

        // if priorities are different return the priority with the smaller number
        return aPriority - bPriority
      }

    case 'sectionName':
      return (a, b) => {
        const aClassName = a.section.data.className
        const aSectionName = a.section.data.sectionName
        const aInstructorName = a.section.instructor.fullName

        const bClassName = b.section.data.className
        const bSectionName = b.section.data.sectionName
        const bInstructorName = b.section.instructor.fullName

        const classComparison = aClassName.localeCompare(bClassName)
        const sectionComparison = aSectionName.localeCompare(bSectionName)
        const instructorComparison =
          aInstructorName.localeCompare(bInstructorName)
        // if class names are the same, compare section names
        // if section names are the same, compare instructor names
        if (classComparison !== 0) return classComparison
        if (sectionComparison !== 0) return sectionComparison
        return instructorComparison
      }
    case 'experienceName':
      return (a, b) =>
        a.slideDeck.data.slideDeckName.localeCompare(
          b.slideDeck.data.slideDeckName
        )
    case 'dueDate':
      return (a, b) => {
        return compareDates(a.assignment.dueDate, b.assignment.dueDate)
      }
    case 'scheduledAt':
      return (a, b) => {
        const aScheduled = a.roomState.scheduledAtDate
        const bScheduled = b.roomState.scheduledAtDate
        // if no scheduled put at end
        if (!aScheduled && !bScheduled) return 0
        if (!aScheduled) return 1
        if (!bScheduled) return -1
        return compareDates(aScheduled.toJSDate(), bScheduled.toJSDate())
      }
    case 'instructorName':
      return (a, b) => {
        const aInstructorName = a.section.instructor.fullName
        const bInstructorName = b.section.instructor.fullName
        return aInstructorName.localeCompare(bInstructorName)
      }
  }
}

function getSortingPriorityFromActionState(
  state: ValidLibraryObjectActionState
): number {
  switch (state) {
    // actionable states — finishing the End-of-Session quiz sits here because
    // its window is short, which also puts it ahead of peer review
    case ValidLibraryObjectActionState.joinSession:
    case ValidLibraryObjectActionState.startSession:
    case ValidLibraryObjectActionState.finishEndOfSessionQuiz:
      return 0
    case ValidLibraryObjectActionState.completePeerReviews:
      return 1
    case ValidLibraryObjectActionState.completeQuiz:
      return 2
    case ValidLibraryObjectActionState.joinGroup:
    case ValidLibraryObjectActionState.joinNewGroup:
      return 3
    case ValidLibraryObjectActionState.scheduleSession:
    case ValidLibraryObjectActionState.enroll:
      return 4

    // non actionable states
    case ValidLibraryObjectActionState.pendingGrouping:
    case ValidLibraryObjectActionState.pendingScheduling:
      return 5
    case ValidLibraryObjectActionState.sessionScheduled:
      return 6
    case ValidLibraryObjectActionState.suspended:
      return 7
    case ValidLibraryObjectActionState.loading:
      return 8
    case ValidLibraryObjectActionState.availableOn:
      return 9
    case ValidLibraryObjectActionState.viewResults:
      return 10
    case ValidLibraryObjectActionState.canceled:
    case ValidLibraryObjectActionState.experienceExpired:
      return 11
  }
}

export const studentLibraryAssignmentFilter = ['all', 'current'] as const
export type StudentLibraryAssignmentFilter =
  (typeof studentLibraryAssignmentFilter)[number]

export type StudentLibraryAssignmentSortingStrategy =
  | 'default'
  | 'sectionName'
  | 'experienceName'
  | 'dueDate'
  | 'scheduledAt'
  | 'instructorName'

export type StudentLibraryAssignmentSortingOrder = 'asc' | 'desc'

/**
 * We have an action type for every LibraryObjectState
 * some have buttons for navigation and some do not
 *
 * we use a switch here for type safety if we add more states
 */
function actionTypeHasActionComponentToRender(
  actionState: ValidLibraryObjectActionState
): boolean {
  switch (actionState) {
    case ValidLibraryObjectActionState.enroll:
    case ValidLibraryObjectActionState.joinGroup:
    case ValidLibraryObjectActionState.joinNewGroup:
    case ValidLibraryObjectActionState.scheduleSession:
    case ValidLibraryObjectActionState.completeQuiz:
    case ValidLibraryObjectActionState.joinSession:
    case ValidLibraryObjectActionState.startSession:
    case ValidLibraryObjectActionState.finishEndOfSessionQuiz:
    case ValidLibraryObjectActionState.completePeerReviews:
      return true

    // viewResults/suspended have action, but never show it in header
    case ValidLibraryObjectActionState.suspended:
    case ValidLibraryObjectActionState.viewResults:
    case ValidLibraryObjectActionState.availableOn:
    case ValidLibraryObjectActionState.sessionScheduled:
    case ValidLibraryObjectActionState.pendingGrouping:
    case ValidLibraryObjectActionState.pendingScheduling:
    case ValidLibraryObjectActionState.experienceExpired:
    case ValidLibraryObjectActionState.loading:
    case ValidLibraryObjectActionState.canceled:
      return false
  }
}
